import React, { useState } from 'react';
import { Shield, Award, Clock, Users, CheckCircle, TrendingUp, Mail, User, Phone, MessageSquare, FileCheck, Scale, Globe, Zap, Target, Lock } from 'lucide-react';
import PricingSection from '../components/PricingSection';
import TestimonialsSlider from '../components/TestimonialsSlider';
import ProcessSteps from '../components/ProcessSteps';
import UniversalPopupForm from '../components/UniversalPopupForm';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { toast } from 'sonner';
import { submitLead, fetchGeolocation } from '../services/apiService';

const Home = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+1',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await submitLead(formData);
      
      if (result.success) {
        toast.success(result.message);
        // Redirect to application page
        window.location.href = `/application/main?customerid=${result.customerId}`;
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChatClick = () => {
    // Zendesk chat widget will handle this
    if (window.zE) {
      window.openZendeskChat && window.openZendeskChat();
    } else {
      toast.info('Chat is loading... Please try again in a moment.');
    }
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Trademarkfilingcanada | Canada Trademark Search and Filing | File in Canada"
        description="Trademarkfilingcanada offers clear guidance for Canadian filing with simple guidance. Stay informed with simple status updates."
        keywords="trademark registration Canada, CIPO trademark services, Canadian IP protection, professional trademark filing, Canadian trademark attorneys"
      />
      
      {/* Hero Section with Form on Right */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          {/* Canada Flag Background */}
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/d/d9/Flag_of_Canada_%28Pantone%29.svg"
            alt="Canada Flag"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/70"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div className="text-white">
              <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                <span className="text-white font-semibold text-sm">🍁 Trademark Filing Canada - Professional Trademark Services</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
                Secure Your Brand with Expert CIPO Registration
              </h1>
              <p className="text-xl text-brand-100 mb-8 leading-relaxed">
                Professional trademark registration services across Canada. Our experienced specialists navigate CIPO requirements with precision, ensuring comprehensive protection for your intellectual property.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => setIsPopupOpen(true)}
                  className="bg-white text-brand-600 font-bold px-8 py-4 rounded-lg hover:bg-brand-50 transform hover:scale-105 transition-all duration-200 shadow-xl"
                >
                  Register My Trademark
                </button>
                <button
                  onClick={handleChatClick}
                  className="bg-brand-700/50 backdrop-blur-sm border-2 border-white text-white font-bold px-8 py-4 rounded-lg hover:bg-brand-700/70 transition-all duration-200"
                >
                  Chat Now
                </button>
              </div>
            </div>

            {/* Right Side - Form (Reduced Height) */}
            <div>
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6">
                <div className="text-center mb-4">
                  <div className="inline-block bg-brand-600 rounded-full p-3 mb-3">
                    <Shield className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900 mb-1">Initiate Your Trademark Application</h2>
                  <p className="text-sm text-gray-600">Professional brand protection services</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <User className="w-3 h-3 inline mr-1" />
                      Full Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-sm"
                      placeholder="John Smith"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Mail className="w-3 h-3 inline mr-1" />
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-sm"
                      placeholder="john@example.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Phone className="w-3 h-3 inline mr-1" />
                      Contact Number *
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={formData.countryCode}
                        onChange={(e) => setFormData({ ...formData, countryCode: e.target.value })}
                        className="w-24 flex-shrink-0 px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-sm"
                      >
                        <option value="+1">🇨🇦 +1 (Canada)</option>
                        <option value="+1">🇺🇸 +1 (USA)</option>
                        <option value="+44">🇬🇧 +44 (UK)</option>
                        <option value="+91">🇮🇳 +91 (India)</option>
                        <option value="+86">🇨🇳 +86 (China)</option>
                        <option value="+81">🇯🇵 +81 (Japan)</option>
                        <option value="+82">🇰🇷 +82 (South Korea)</option>
                        <option value="+61">🇦🇺 +61 (Australia)</option>
                        <option value="+49">🇩🇪 +49 (Germany)</option>
                        <option value="+33">🇫🇷 +33 (France)</option>
                        <option value="+39">🇮🇹 +39 (Italy)</option>
                        <option value="+34">🇪🇸 +34 (Spain)</option>
                        <option value="+7">🇷🇺 +7 (Russia)</option>
                        <option value="+55">🇧🇷 +55 (Brazil)</option>
                        <option value="+52">🇲🇽 +52 (Mexico)</option>
                        <option value="+27">🇿🇦 +27 (South Africa)</option>
                        <option value="+971">🇦🇪 +971 (UAE)</option>
                        <option value="+65">🇸🇬 +65 (Singapore)</option>
                        <option value="+31">🇳🇱 +31 (Netherlands)</option>
                        <option value="+46">🇸🇪 +46 (Sweden)</option>
                      </select>
                      <input
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          if (value.length <= 15) {
                            setFormData({ ...formData, phone: value });
                          }
                        }}
                        className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-sm"
                        placeholder="4165550123"
                        minLength="10"
                        maxLength="15"
                        pattern="[0-9]{10,15}"
                        title="Please enter a valid phone number (10-15 digits)"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <MessageSquare className="w-3 h-3 inline mr-1" />
                      Message
                    </label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all resize-none text-sm"
                      placeholder="Tell us about your trademark..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-brand-600 to-brand-700 text-white font-semibold py-3 px-6 rounded-lg hover:from-brand-700 hover:to-brand-800 transform hover:scale-[1.02] transition-all duration-200 disabled:opacity-50 shadow-lg text-sm"
                  >
                    {loading ? 'Processing...' : 'Request Consultation'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-brand-600 mb-2">1000+</div>
              <div className="text-gray-600">Successful Registrations</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-brand-600 mb-2">99%</div>
              <div className="text-gray-600">Approval Rate</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-brand-600 mb-2">15+</div>
              <div className="text-gray-600">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-brand-600 mb-2">10</div>
              <div className="text-gray-600">Provinces Served</div>
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section 1 - Image Left, Content Right */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-stretch">
            {/* Image Column - Full Height */}
            <div className="relative group cursor-pointer">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-600 to-brand-800 rounded-2xl opacity-10 blur-2xl group-hover:opacity-20 transition-opacity duration-300"></div>
              <img
                src="https://images.unsplash.com/photo-1606836591695-4d58a73eba1e?w=800"
                alt="Professional trademark consultation"
                className="relative rounded-2xl shadow-2xl w-full h-full min-h-[600px] object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute bottom-6 left-6 bg-white rounded-xl shadow-xl p-4 group-hover:shadow-2xl group-hover:scale-105 transition-all duration-300">
                <div className="flex items-center space-x-3">
                  <div className="bg-brand-100 rounded-full p-2 group-hover:bg-brand-600 transition-colors duration-300">
                    <CheckCircle className="w-5 h-5 text-brand-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">CIPO Certified</div>
                    <p className="text-sm text-gray-600">Expert guidance for seamless approval</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Column */}
            <div className="flex flex-col">
              <div className="inline-block bg-brand-100 text-brand-700 rounded-full px-4 py-2 mb-4 text-sm font-semibold self-start">
                Professional CIPO Services
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Navigate Canadian Trademark Law with Confidence
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                The Canadian Intellectual Property Office maintains rigorous examination standards. Our experienced professionals have successfully guided over 1,000 trademark applications through the CIPO registration process, delivering consistent results through meticulous attention to regulatory requirements.
              </p>
              <div className="space-y-6 flex-grow mb-8">
                <div className="flex items-start space-x-4 group hover:bg-brand-50 p-4 rounded-xl transition-all duration-300 cursor-pointer">
                  <div className="bg-brand-100 rounded-lg p-3 flex-shrink-0 group-hover:bg-brand-600 group-hover:scale-110 transition-all duration-300">
                    <FileCheck className="w-6 h-6 text-brand-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 group-hover:text-brand-600 transition-colors duration-300">Comprehensive Documentation</h3>
                    <p className="text-gray-600">Thorough preparation addressing all CIPO requirements to optimize application success.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 group hover:bg-brand-50 p-4 rounded-xl transition-all duration-300 cursor-pointer">
                  <div className="bg-brand-100 rounded-lg p-3 flex-shrink-0 group-hover:bg-brand-600 group-hover:scale-110 transition-all duration-300">
                    <Zap className="w-6 h-6 text-brand-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 group-hover:text-brand-600 transition-colors duration-300">Efficient Processing</h3>
                    <p className="text-gray-600">Systematic approach to deadline management and examiner communications.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 group hover:bg-brand-50 p-4 rounded-xl transition-all duration-300 cursor-pointer">
                  <div className="bg-brand-100 rounded-lg p-3 flex-shrink-0 group-hover:bg-brand-600 group-hover:scale-110 transition-all duration-300">
                    <Target className="w-6 h-6 text-brand-600 group-hover:text-white transition-colors duration-300" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 group-hover:text-brand-600 transition-colors duration-300">Strategic Classification</h3>
                    <p className="text-gray-600">Expert class selection ensures comprehensive protection aligned with business objectives.</p>
                  </div>
                </div>
              </div>
              
              {/* CTA Button */}
              <button
                onClick={() => setIsPopupOpen(true)}
                className="bg-gradient-to-r from-brand-600 to-brand-800 text-white font-semibold px-8 py-4 rounded-lg hover:from-brand-700 hover:to-brand-900 transform hover:scale-105 transition-all duration-200 shadow-lg self-start"
              >
                Start Your Application
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section 2 - Content Left, Image Right */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="order-2 lg:order-1 flex flex-col justify-between h-full min-h-[600px]">
              <div>
                <div className="inline-block bg-brand-100 text-brand-700 rounded-full px-4 py-2 mb-6 text-sm font-semibold w-fit">
                  Federal Protection
                </div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  Comprehensive National Trademark Rights
                </h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Federal trademark registration through CIPO establishes exclusive rights across all Canadian provinces and territories. Professional registration provides comprehensive legal protection for your business identity and brand assets.
                </p>
                <div className="bg-gradient-to-br from-brand-50 to-brand-100 rounded-2xl p-8 mb-8">
                  <h3 className="font-bold text-gray-900 mb-4 text-xl">Registration Benefits:</h3>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <div className="bg-brand-600 rounded-full p-1">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-gray-700">Exclusive federal rights to mark usage</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="bg-brand-600 rounded-full p-1">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-gray-700">Legal standing for infringement proceedings</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="bg-brand-600 rounded-full p-1">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-gray-700">Renewable 10-year protection terms</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <div className="bg-brand-600 rounded-full p-1">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <span className="text-gray-700">Transferable intellectual property asset</span>
                    </div>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setIsPopupOpen(true)}
                className="bg-gradient-to-r from-brand-600 to-brand-700 text-white font-semibold px-8 py-4 rounded-lg hover:from-brand-700 hover:to-brand-800 hover:shadow-2xl transform hover:scale-105 transition-all duration-300 shadow-lg w-fit mt-auto"
              >
                Initiate Application
              </button>
            </div>

            <div className="order-1 lg:order-2 relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-400 to-brand-600 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0"
                alt="Business team celebrating success"
                className="relative rounded-2xl shadow-2xl w-full h-[600px] object-cover"
              />
              <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl p-4">
                <div className="flex items-center space-x-3">
                  <div className="bg-brand-600 rounded-full p-2">
                    <Lock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Federal Registration</div>
                    <div className="text-sm text-gray-600">CIPO Certified</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section 3 - Image Left, Content Right */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-purple-600 to-brand-600 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1556761175-4b46a572b786"
                alt="Legal consultation session"
                className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -top-6 -left-6 bg-gradient-to-r from-brand-600 to-brand-800 text-white rounded-xl shadow-xl p-6 max-w-xs">
                <Scale className="w-8 h-8 mb-2" />
                <div className="font-bold text-lg">Professional Standards</div>
                <div className="text-sm text-brand-100">Experienced trademark specialists</div>
              </div>
            </div>

            <div>
              <div className="inline-block bg-purple-100 text-purple-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Professional Advantage
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                The Value of Expert Guidance
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Independent trademark applications face significantly higher refusal rates. Our specialists apply comprehensive knowledge of CIPO examination standards to optimize application success and avoid costly procedural errors.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-red-100">
                  <div className="text-3xl font-bold text-red-600 mb-2">42%</div>
                  <div className="text-sm text-gray-600">Self-Filed Refusal Rate</div>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-green-100">
                  <div className="text-3xl font-bold text-green-600 mb-2">99%</div>
                  <div className="text-sm text-gray-600">Professional Success Rate</div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-brand-600 flex-shrink-0" />
                  <span className="text-gray-700">Avoid procedural errors and application fees</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-brand-600 flex-shrink-0" />
                  <span className="text-gray-700">Efficient examiner communications and responses</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-brand-600 flex-shrink-0" />
                  <span className="text-gray-700">Strategic application preparation for optimal outcomes</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section 4 - Content Left, Image Right */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block bg-brand-100 text-brand-700 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Trusted Across Canada
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Trusted by Canadian Enterprises
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                Organizations across Canada rely on our professional trademark services to protect their intellectual property. Our systematic approach and expertise in CIPO procedures have established a proven track record of successful registrations.
              </p>
              <div className="space-y-6">
                <div className="flex items-start space-x-4 group hover:bg-white/80 p-4 rounded-xl transition-all duration-300 cursor-pointer hover:shadow-lg">
                  <div className="bg-gradient-to-r from-brand-600 to-brand-700 rounded-full p-3 flex-shrink-0 group-hover:scale-110 group-hover:shadow-xl transition-all duration-300">
                    <Globe className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-lg group-hover:text-brand-600 transition-colors duration-300">National Service Coverage</h3>
                    <p className="text-gray-600">Professional trademark services across all Canadian provinces and territories.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 group hover:bg-white/80 p-4 rounded-xl transition-all duration-300 cursor-pointer hover:shadow-lg">
                  <div className="bg-gradient-to-r from-brand-600 to-brand-700 rounded-full p-3 flex-shrink-0 group-hover:scale-110 group-hover:shadow-xl transition-all duration-300">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-lg group-hover:text-brand-600 transition-colors duration-300">Proven Track Record</h3>
                    <p className="text-gray-600">Established expertise serving diverse industries and business sectors.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4 group hover:bg-white/80 p-4 rounded-xl transition-all duration-300 cursor-pointer hover:shadow-lg">
                  <div className="bg-gradient-to-r from-brand-600 to-brand-700 rounded-full p-3 flex-shrink-0 group-hover:scale-110 group-hover:shadow-xl transition-all duration-300">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1 text-lg group-hover:text-brand-600 transition-colors duration-300">Professional Management</h3>
                    <p className="text-gray-600">Dedicated case coordination throughout the registration process.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2 relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-400 to-brand-600 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c"
                alt="Professional business team"
                className="relative rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                <div className="text-4xl font-bold text-brand-600 mb-1">1000+</div>
                <div className="text-gray-700 font-semibold">Successful Registrations</div>
                <div className="text-sm text-gray-500 mt-1">Nationwide coverage</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Professional Trademark Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Comprehensive expertise in Canadian trademark law and CIPO registration procedures
            </p>
          </div>

          <div className="md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto snap-x snap-mandatory gap-4 pb-4 md:pb-0 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-brand-600 min-w-full md:min-w-0 snap-center transform hover:scale-105 cursor-pointer group">
              <div className="bg-brand-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:bg-brand-600 transition-all duration-300">
                <Shield className="w-7 h-7 text-brand-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-brand-600 transition-colors duration-300">CIPO Specialists</h3>
              <p className="text-gray-600 leading-relaxed">
                Comprehensive knowledge of Canadian Intellectual Property Office requirements and examination standards ensuring efficient application processing.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-brand-600 min-w-full md:min-w-0 snap-center transform hover:scale-105 cursor-pointer group">
              <div className="bg-brand-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:bg-brand-600 transition-all duration-300">
                <Award className="w-7 h-7 text-brand-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-brand-600 transition-colors duration-300">Proven Success Rate</h3>
              <p className="text-gray-600 leading-relaxed">
                99% approval rate achieved through thorough clearance searches and strategic application preparation.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-brand-600 min-w-full md:min-w-0 snap-center transform hover:scale-105 cursor-pointer group">
              <div className="bg-brand-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:bg-brand-600 transition-all duration-300">
                <Clock className="w-7 h-7 text-brand-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-brand-600 transition-colors duration-300">Efficient Processing</h3>
              <p className="text-gray-600 leading-relaxed">
                Professional service standards with systematic deadline management and responsive client communications.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-brand-600 min-w-full md:min-w-0 snap-center transform hover:scale-105 cursor-pointer group">
              <div className="bg-brand-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:bg-brand-600 transition-all duration-300">
                <Users className="w-7 h-7 text-brand-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-brand-600 transition-colors duration-300">Professional Coordination</h3>
              <p className="text-gray-600 leading-relaxed">
                Experienced case management guiding clients through each phase of the Canadian trademark registration process.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-brand-600 min-w-full md:min-w-0 snap-center transform hover:scale-105 cursor-pointer group">
              <div className="bg-brand-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:bg-brand-600 transition-all duration-300">
                <CheckCircle className="w-7 h-7 text-brand-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-brand-600 transition-colors duration-300">Thorough Clearance Research</h3>
              <p className="text-gray-600 leading-relaxed">
                Comprehensive database searches identifying potential conflicts and assessing mark registrability.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-brand-600 min-w-full md:min-w-0 snap-center transform hover:scale-105 cursor-pointer group">
              <div className="bg-brand-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:bg-brand-600 transition-all duration-300">
                <TrendingUp className="w-7 h-7 text-brand-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-brand-600 transition-colors duration-300">Portfolio Management</h3>
              <p className="text-gray-600 leading-relaxed">
                Systematic monitoring services protecting registered marks from potential infringement and unauthorized use.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps */}
      <ProcessSteps />

      {/* Pricing Section */}
      <div id="pricing">
        <PricingSection />
      </div>

      {/* Testimonials */}
      <TestimonialsSlider />

      {/* Final CTA Section with Canadian Location Background */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab"
            alt="Toronto skyline and waterfront"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-900/95 via-brand-800/90 to-brand-700/95"></div>
          {/* Animated overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-brand-900/50 to-transparent"></div>
        </div>
        
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Floating badge */}
          <div className="inline-block animate-bounce mb-8">
            <div className="bg-white/20 backdrop-blur-md rounded-full px-6 py-3 border-2 border-white/30">
              <span className="text-white font-bold text-sm">🍁 Professional Canadian Trademark Services</span>
            </div>
          </div>

          <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Secure Your Intellectual Property<br />
            <span className="bg-gradient-to-r from-brand-300 to-brand-400 bg-clip-text text-transparent">
              with Federal Trademark Registration
            </span>
          </h2>
          
          <p className="text-2xl text-brand-100 mb-12 max-w-3xl mx-auto leading-relaxed">
            Professional CIPO registration services for Canadian businesses. 
            Establish comprehensive brand protection with experienced trademark specialists.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
            <button
              onClick={() => setIsPopupOpen(true)}
              className="group relative bg-white text-brand-600 font-bold px-10 py-5 rounded-xl hover:bg-gray-100 transform hover:scale-110 transition-all duration-300 shadow-2xl"
            >
              <span className="relative z-10 flex items-center space-x-2">
                <Shield className="w-6 h-6" />
                <span className="text-lg">Initiate Application</span>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-brand-400 to-brand-500 rounded-xl opacity-0 group-hover:opacity-20 blur transition-opacity duration-300"></div>
            </button>
            
            <button
              onClick={handleChatClick}
              className="group border-3 border-white text-white font-bold px-10 py-5 rounded-xl hover:bg-white hover:text-brand-600 transition-all duration-300 shadow-2xl backdrop-blur-sm"
            >
              <span className="flex items-center space-x-2">
                <span className="text-lg">Chat Now</span>
                <span className="group-hover:translate-x-1 transition-transform duration-300">→</span>
              </span>
            </button>
          </div>

          {/* Trust indicators */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">1000+</div>
              <div className="text-brand-100 text-sm">Registrations</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">99%</div>
              <div className="text-brand-100 text-sm">Success Rate</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">15+</div>
              <div className="text-brand-100 text-sm">Years Experience</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20">
              <div className="text-4xl font-bold text-white mb-2">10</div>
              <div className="text-brand-100 text-sm">Provinces Served</div>
            </div>
          </div>

          <p className="text-brand-200 mt-12 text-sm">
            Professional trademark services nationwide • CIPO Registration Specialists
          </p>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-10 left-10 w-20 h-20 bg-brand-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-32 h-32 bg-brand-400/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </section>

      <UniversalPopupForm isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Home;
